import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useI18n } from "../context/I18nContext";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { items, setQty, remove, total } = useCart();
  const { t, lang } = useI18n();
  const navigate = useNavigate();

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-24 text-center" data-testid="cart-empty">
        <ShoppingBag className="w-12 h-12 mx-auto text-slate-400" />
        <h1 className="font-display font-black text-3xl mt-4">{t("cart.empty")}</h1>
        <Link to="/shop" className="inline-block mt-6">
          <button className="btn-maroon px-6 py-3 font-bold">{t("cart.shopNow")}</button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <h1 className="font-display font-black text-4xl tracking-tighter mb-8">{t("cart.title")}</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-10">
        <div className="border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="label-mono text-slate-500">
                <th className="text-start p-4">Item</th>
                <th className="p-4">Qty</th>
                <th className="text-end p-4">Price</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.product.id} className="border-b border-slate-200" data-testid={`cart-row-${it.product.sku}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img src={it.product.image_url} className="w-16 h-16 object-cover border border-slate-200" alt="" />
                      <div>
                        <div className="font-display font-bold">{lang === "ar" ? it.product.name_ar : it.product.name_en}</div>
                        <div className="label-mono text-slate-500 mt-1">{it.product.sku}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center border border-slate-300 w-fit mx-auto">
                      <button onClick={() => setQty(it.product.id, it.qty - 1)} className="px-2 py-2 hover:bg-slate-100"><Minus className="w-3 h-3" /></button>
                      <input
                        type="number"
                        value={it.qty}
                        onChange={(e) => setQty(it.product.id, parseInt(e.target.value || 1))}
                        className="w-12 text-center outline-none py-2 font-bold text-sm"
                      />
                      <button onClick={() => setQty(it.product.id, it.qty + 1)} className="px-2 py-2 hover:bg-slate-100"><Plus className="w-3 h-3" /></button>
                    </div>
                  </td>
                  <td className="p-4 text-end font-bold">
                    {(it.product.price * it.qty).toFixed(2)} {t("common.currency")}
                  </td>
                  <td className="p-4">
                    <button onClick={() => remove(it.product.id)} className="text-slate-400 hover:text-red-600" data-testid={`remove-${it.product.sku}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="border border-slate-200 p-6 h-fit sticky top-24">
          <div className="label-mono text-slate-500">{t("cart.title")}</div>
          <div className="flex justify-between mt-4 text-sm">
            <span>{t("cart.subtotal")}</span>
            <span className="font-bold">{total.toFixed(2)} {t("common.currency")}</span>
          </div>
          <div className="border-t border-slate-200 mt-4 pt-4 flex justify-between items-end">
            <span className="font-display font-bold text-lg">{t("cart.total")}</span>
            <span className="font-display font-black text-2xl text-[#8D1B3D]">
              {total.toFixed(2)} {t("common.currency")}
            </span>
          </div>
          <button
            onClick={() => navigate("/checkout")}
            className="btn-maroon w-full mt-6 py-4 font-bold tracking-wide"
            data-testid="checkout-btn"
          >
            {t("cart.checkout")}
          </button>
        </aside>
      </div>
    </div>
  );
}
